import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { X, FileDown, Save, Loader2, ChevronLeft, ChevronRight, AlertTriangle, Copy, ImageOff } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass, useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { HexColorPicker } from 'react-colorful'
import { reportExportError, reportExportSuccess, classifyExportError, describeExportCause, exportErrorStage } from '../client/export-errors.js'
import { computePageBreaks, parsePageRange } from '../client/export-pagination.js'
import { checkExportFeasibility } from '../client/export-feasibility.js'
import { PAGE_DIMS, VIEWPORT_WIDTH, hexToRgb } from '../client/export-capture.js'
import { MAX_JPEG_QUALITY } from '../client/htmlToPdf.js'
import { useExportSession, PREVIEW_SCALE } from './useExportSession.js'

export interface PdfExportOptions {
  format: 'pdf' | 'png' | 'jpeg'
  pageSize: 'auto' | 'a4' | 'letter' | 'legal'
  orientation: 'portrait' | 'landscape'
  bgColor: string // hex color or 'auto'
  /** How empty page areas are filled */
  bgFill: 'solid' | 'tile' | 'extend'
  fitToPage: boolean
  /** JPEG quality 0-1 (only for jpeg format) */
  jpegQuality: number
  /** Background opacity 0-1 (only for PNG with custom color) */
  bgAlpha: number
  /** Capture viewport width in px. Lower values simulate narrower
   *  devices (mobile/tablet). Defaults to VIEWPORT_WIDTH (1280) which
   *  approximates a standard desktop. */
  captureWidth?: number
  /** Page margin in mm (PDF only). Default 0. */
  marginMm?: number
  /** Optional header/footer content (PDF only; requires a margin). */
  headerFooter?: { title?: boolean; date?: boolean; pageNumbers?: boolean }
  /** Page-range selector, e.g. "1-3,5" (PDF multi-page only). Empty = all. */
  pageRange?: string
  /** Document title for the header (supplied by the editor, not the dialog UI). */
  documentTitle?: string
  /** Pre-formatted date label for the footer (supplied by the caller). */
  exportDateLabel?: string
}

/** Persisted subset of options ("remember last used"). Enum/number values only. */
export type PersistedExportOptions = Pick<PdfExportOptions,
  'format' | 'pageSize' | 'orientation' | 'bgColor' | 'bgFill' | 'fitToPage' | 'jpegQuality' | 'bgAlpha' | 'marginMm'>

const PAGE_SIZES: { value: PdfExportOptions['pageSize']; label: string }[] = [
  { value: 'auto', label: 'Fit Content (single page)' },
  { value: 'a4', label: 'A4 (210 × 297 mm)' },
  { value: 'letter', label: 'Letter (8.5 × 11 in)' },
  { value: 'legal', label: 'Legal (8.5 × 14 in)' },
]

const MARGIN_PRESETS: { value: number; i18nKey: string }[] = [
  { value: 0, i18nKey: 'export.marginNone' },
  { value: 10, i18nKey: 'export.marginNormal' },
  { value: 20, i18nKey: 'export.marginWide' },
]

interface PdfExportDialogProps {
  htmlContent: string
  parentId?: string | null
  /** File being exported — target for debug-log error reporting. */
  fileId?: string
  /** True when htmlContent is raw source (snapshot failed) — enables sanitize + warning. */
  snapshotFallback?: boolean
  /** True while the caller is still preparing the snapshot — show a spinner,
   *  don't build the capture session yet. */
  preparing?: boolean
  /** Width (px) of the live preview iframe at the moment Export was clicked. */
  initialCaptureWidth?: number
  /** Document title / date for optional header-footer. */
  documentTitle?: string
  exportDateLabel?: string
  /** Seed the controls from the user's last-used options. */
  initialOptions?: Partial<PersistedExportOptions>
  onDownload: (blob: Blob, options: PdfExportOptions) => void
  onSaveToDrive?: (blob: Blob, options: PdfExportOptions) => void
  /** Called with the options actually used, on a successful export (persist last-used). */
  onPersistOptions?: (options: PersistedExportOptions) => void
  onClose: () => void
}

export function PdfExportDialog({
  htmlContent, parentId, fileId, snapshotFallback, preparing, initialCaptureWidth,
  documentTitle, exportDateLabel, initialOptions,
  onDownload, onSaveToDrive, onPersistOptions, onClose,
}: PdfExportDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const { t } = useTranslation('collab')

  const [format, setFormat] = useState<PdfExportOptions['format']>(initialOptions?.format ?? 'pdf')
  const [pageSize, setPageSize] = useState<PdfExportOptions['pageSize']>(initialOptions?.pageSize ?? 'a4')
  const [orientation, setOrientation] = useState<PdfExportOptions['orientation']>(initialOptions?.orientation ?? 'portrait')
  const [bgColor, setBgColor] = useState(initialOptions?.bgColor ?? 'auto')
  const [bgAlpha, setBgAlpha] = useState(initialOptions?.bgAlpha ?? 1)
  const [bgFill, setBgFill] = useState<PdfExportOptions['bgFill']>(initialOptions?.bgFill ?? 'extend')
  const [fitToPage, setFitToPage] = useState(initialOptions?.fitToPage ?? false)
  const [jpegQuality, setJpegQuality] = useState(Math.min(MAX_JPEG_QUALITY, initialOptions?.jpegQuality ?? 0.92))
  const [marginMm, setMarginMm] = useState(initialOptions?.marginMm ?? 0)
  const [hfTitle, setHfTitle] = useState(false)
  const [hfDate, setHfDate] = useState(false)
  const [hfPageNumbers, setHfPageNumbers] = useState(false)
  const [pageRange, setPageRange] = useState('')
  const [captureWidth, setCaptureWidth] = useState(
    initialCaptureWidth ? Math.max(320, Math.min(1920, Math.round(initialCaptureWidth))) : VIEWPORT_WIDTH,
  )
  const [debouncedCaptureWidth, setDebouncedCaptureWidth] = useState(captureWidth)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCaptureWidth(captureWidth), 300)
    return () => clearTimeout(id)
  }, [captureWidth])
  const [showPicker, setShowPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  const [previewPage, setPreviewPage] = useState(0)
  const [generating, setGenerating] = useState(false)
  const previewRef = useRef<HTMLCanvasElement>(null)

  // ── The single capture session (preview + export render against it) ──
  const session = useExportSession(
    htmlContent, parentId ?? null, fileId ?? '', !!snapshotFallback, debouncedCaptureWidth, bgFill, !preparing,
  )
  const { sourceCanvas, previewBg, autoBgColor, contentHeight, breakRects, warnings, retry, exportBlob } = session

  const isImage = format === 'png' || format === 'jpeg'
  const showAssetsWarning = warnings.some((w) => w.kind === 'snapshot-fallback' || w.kind === 'resource-timeout')

  // Page geometry (mm). Auto/image fit content aspect; fixed sizes honor orientation.
  const autoPageH = sourceCanvas ? (sourceCanvas.height / sourceCanvas.width) * 210 : 297
  const pageDims = useMemo(() => {
    if (isImage || pageSize === 'auto') return { w: 210, h: autoPageH }
    const [w, h] = PAGE_DIMS[pageSize]
    return orientation === 'portrait' ? { w, h } : { w: h, h: w }
  }, [isImage, pageSize, orientation, autoPageH])

  // Page breaks (CSS px) — shared with the export so preview page N == export page N.
  const singlePage = isImage || pageSize === 'auto' || fitToPage
  const pageContentCss = useMemo(() => {
    if (singlePage) return contentHeight || 1
    const boxW = pageDims.w - 2 * marginMm
    const boxH = pageDims.h - 2 * marginMm
    if (boxW <= 0 || boxH <= 0) return contentHeight || 1
    return debouncedCaptureWidth * boxH / boxW
  }, [singlePage, pageDims, marginMm, debouncedCaptureWidth, contentHeight])
  const pageBounds = useMemo(() => {
    if (singlePage || !contentHeight) return [contentHeight || 1]
    return computePageBreaks(breakRects, contentHeight, pageContentCss)
  }, [singlePage, contentHeight, breakRects, pageContentCss])
  const pageCount = pageBounds.length

  const buildOptions = useCallback((): PdfExportOptions => ({
    format, pageSize, orientation, bgColor, bgFill, fitToPage, jpegQuality, bgAlpha,
    captureWidth: debouncedCaptureWidth, marginMm,
    headerFooter: (hfTitle || hfDate || hfPageNumbers) ? { title: hfTitle, date: hfDate, pageNumbers: hfPageNumbers } : undefined,
    pageRange: pageRange.trim() || undefined,
    documentTitle, exportDateLabel,
  }), [format, pageSize, orientation, bgColor, bgFill, fitToPage, jpegQuality, bgAlpha, debouncedCaptureWidth, marginMm, hfTitle, hfDate, hfPageNumbers, pageRange, documentTitle, exportDateLabel])

  const persisted = useCallback((o: PdfExportOptions): PersistedExportOptions => ({
    format: o.format, pageSize: o.pageSize, orientation: o.orientation, bgColor: o.bgColor,
    bgFill: o.bgFill, fitToPage: o.fitToPage, jpegQuality: o.jpegQuality, bgAlpha: o.bgAlpha, marginMm: o.marginMm,
  }), [])

  // Close color picker on click outside
  useEffect(() => {
    if (!showPicker) return
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setShowPicker(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showPicker])

  // Keep previewPage in range as page count changes.
  useEffect(() => { setPreviewPage((p) => Math.min(p, Math.max(0, pageCount - 1))) }, [pageCount])

  // ── Draw the thumbnail (slices the shared low-res sourceCanvas by page) ──
  useEffect(() => {
    if (!sourceCanvas || !previewRef.current) return
    const preview = previewRef.current
    const ctx = preview.getContext('2d')!

    const scaleByH = 200 / pageDims.h
    const scaleByW = 160 / pageDims.w
    const scale = Math.min(scaleByH, scaleByW)
    const pW = Math.round(pageDims.w * scale)
    const pH = Math.round(pageDims.h * scale)
    preview.width = pW
    preview.height = pH

    const drawCheckerboard = () => {
      const sz = 6
      for (let y = 0; y < pH; y += sz) for (let x = 0; x < pW; x += sz) {
        ctx.fillStyle = ((x / sz + y / sz) % 2 === 0) ? '#ccc' : '#fff'
        ctx.fillRect(x, y, sz, sz)
      }
    }

    const showTransparent = bgColor === 'transparent' && format === 'png'
    if (showTransparent) {
      drawCheckerboard()
    } else if ((bgFill === 'tile' || bgFill === 'extend') && previewBg && !isImage) {
      // Slice with the EXPORT's stride (pageContentCss per page), not
      // previewBg.height/pageCount — the export's bg layer spans
      // pageCount×pageContentCss (≥ contentHeight, breaks only nudge up), so a
      // uniform division sampled earlier offsets than the exported pages. The
      // preview layer only covers contentHeight; the sliver past it (last
      // page's nudge slack) draws proportionally shorter — thumbnail-only.
      const bgScale = contentHeight > 0 ? previewBg.height / contentHeight : PREVIEW_SCALE
      const bgStripH = pageContentCss * bgScale
      const bgSrcY = previewPage * bgStripH
      const bgSrcH = Math.min(bgStripH, previewBg.height - bgSrcY)
      if (bgSrcH > 0) ctx.drawImage(previewBg, 0, bgSrcY, previewBg.width, bgSrcH, 0, 0, pW, pH * (bgSrcH / bgStripH))
    } else {
      const bg = bgColor === 'auto' ? autoBgColor : bgColor
      const [r, g, b] = hexToRgb(bg)
      const a = (format === 'png' && bgColor !== 'transparent') ? bgAlpha : 1
      if (a < 1) drawCheckerboard()
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`
      ctx.fillRect(0, 0, pW, pH)
    }

    if (isImage && previewBg && (bgFill === 'extend' || bgFill === 'tile')) {
      ctx.drawImage(previewBg, 0, 0, previewBg.width, previewBg.height, 0, 0, pW, pH)
    }

    if (singlePage) {
      // Fit entire content into the page/image (preserve aspect ratio)
      const contentAR = sourceCanvas.width / sourceCanvas.height
      const pageAR = pW / pH
      let dw: number, dh: number
      if (contentAR > pageAR) { dw = pW; dh = pW / contentAR } else { dh = pH; dw = pH * contentAR }
      ctx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height, (pW - dw) / 2, (pH - dh) / 2, dw, dh)
    } else {
      // Page-break-aware slice: this page covers [top, bottom) CSS px of content.
      const top = previewPage === 0 ? 0 : pageBounds[previewPage - 1]
      const bottom = pageBounds[previewPage]
      const srcY = top * PREVIEW_SCALE
      const srcH = Math.min((bottom - top) * PREVIEW_SCALE, sourceCanvas.height - srcY)
      // Margin inset (fraction of the page box), best-effort for the thumbnail.
      const insetX = pageDims.w > 0 ? (marginMm / pageDims.w) * pW : 0
      const insetY = pageDims.h > 0 ? (marginMm / pageDims.h) * pH : 0
      const boxW = pW - 2 * insetX
      const dstH = srcH * (boxW / sourceCanvas.width)
      if (srcH > 0) ctx.drawImage(sourceCanvas, 0, srcY, sourceCanvas.width, srcH, insetX, insetY, boxW, dstH)
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.15)'
    ctx.lineWidth = 1
    ctx.strokeRect(0.5, 0.5, pW - 1, pH - 1)
  }, [sourceCanvas, previewBg, format, pageSize, orientation, bgColor, bgAlpha, bgFill, autoBgColor, fitToPage, pageDims, previewPage, pageBounds, pageCount, singlePage, isImage, marginMm])

  // The typed page range, resolved against the current page count. `invalid`
  // covers both bad syntax and a range that selects zero pages ('5-9' on a
  // 3-page doc) — without this the export silently ignored the range and
  // produced ALL pages (parsePageRange returns null for both "no filter" and
  // "selects nothing", and htmlToPdf treats null as no-filter).
  const rangeActive = format === 'pdf' && !singlePage && pageRange.trim() !== ''
  const selectedPages = useMemo(
    () => (rangeActive ? parsePageRange(pageRange, pageCount) : null),
    [rangeActive, pageRange, pageCount],
  )
  const pageRangeInvalid = rangeActive && selectedPages === null

  // ── Export runners ──
  const runExport = useCallback(async (deliver: (blob: Blob, options: PdfExportOptions) => void) => {
    const options = buildOptions()
    setGenerating(true)
    // The toast total must match what the export will emit: a page range
    // exports selected.length pages, not pageCount.
    const totalPages = selectedPages?.length ?? pageCount
    const controller = totalPages > 3 && !isImage ? toast.progress(t('export.progress'), totalPages) : null
    const startedAt = performance.now()
    try {
      const { blob, engine, pages } = await exportBlob(options, (p) => controller?.update(p))
      controller?.complete()
      deliver(blob, options)
      onPersistOptions?.(persisted(options))
      // Info breadcrumb: which engine served the user + how long — lets the
      // assistant diagnose "it worked yesterday" regressions.
      reportExportSuccess({ fileId: fileId ?? '', engine, options, pages, ms: performance.now() - startedAt, bytes: blob.size })
    } catch (e) {
      controller?.fail('Export failed')
      // ExportFault carries the true failing stage; 'assemble' is only the fallback.
      reportExportError(exportErrorStage(e, 'assemble'), e, { fileId: fileId ?? '', options, dims: { captureWidth: debouncedCaptureWidth, contentHeight, pageCount } })
      // Give the user a short idea of what went wrong, when we can name it;
      // fall back to the fully generic message otherwise.
      const reasonKey = describeExportCause(classifyExportError(e))
      toast.error(reasonKey
        ? t('export.failedWithReason', { reason: t(`export.reason.${reasonKey}`) })
        : t('export.failedGeneric'))
    } finally {
      setGenerating(false)
    }
  }, [buildOptions, pageCount, selectedPages, isImage, toast, exportBlob, onPersistOptions, persisted, fileId, debouncedCaptureWidth, contentHeight])

  const handleCopyImage = useCallback(async () => {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      toast.error(t('export.copyUnsupported'))
      return
    }
    setGenerating(true)
    try {
      const { blob } = await exportBlob({ ...buildOptions(), format: 'png' })
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      toast.success(t('export.copySuccess'))
    } catch (e) {
      reportExportError(exportErrorStage(e, 'deliver'), e, { fileId: fileId ?? '' })
      toast.error(t('export.copyFailed'))
    } finally {
      setGenerating(false)
    }
  }, [exportBlob, buildOptions, toast, fileId])

  const previewReady = session.status === 'ready' && !!sourceCanvas
  const previewErrored = session.status === 'error'

  // Pre-flight: the single-canvas modes (PNG/JPEG, and PDF page "auto" /
  // fit-to-page) render the whole document to one canvas; if that would exceed
  // the browser's canvas dimension cap the export fails at Download. We know the
  // dimensions now, so predict it and block the export UP FRONT. Deps are ONLY
  // the options that change those dimensions — format, page geometry, capture
  // width, content height — not colors/quality/header-footer/page-range.
  const feasibility = useMemo(
    () => checkExportFeasibility({ format, pageSize, orientation, fitToPage, marginMm, captureWidth: debouncedCaptureWidth, contentHeight }),
    [format, pageSize, orientation, fitToPage, marginMm, debouncedCaptureWidth, contentHeight],
  )
  const exportBlocked = previewReady && !feasibility.ok
  // Copy always renders a PNG regardless of the selected format, so it needs
  // its own gate — a feasible multi-page PDF selection says nothing about
  // whether the single-canvas PNG can be produced.
  const copyFeasible = useMemo(
    () => checkExportFeasibility({ format: 'png', captureWidth: debouncedCaptureWidth, contentHeight }).ok,
    [debouncedCaptureWidth, contentHeight],
  )
  const copyBlocked = previewReady && !copyFeasible

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className={`${themeClass} relative themed-bg-secondary rounded-lg shadow-2xl w-[520px] flex flex-col border themed-border`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h3 className="text-sm font-medium themed-text">{t('export.title')}</h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {showAssetsWarning && !exportBlocked && (
          <div className="flex items-start gap-2 px-4 py-2 text-[11px] bg-amber-500/10 border-b border-amber-500/30 text-amber-500">
            <AlertTriangle size={13} className="mt-px shrink-0" />
            <span>{t('export.assetsMissingWarning')}</span>
          </div>
        )}

        {exportBlocked && (
          <div className="flex items-start gap-2 px-4 py-2 text-[11px] bg-red-500/10 border-b border-red-500/30 text-red-400">
            <AlertTriangle size={13} className="mt-px shrink-0" />
            <span>
              {/* Multi-page PDF is never blocked, so a blocked PDF is always the
                  single-page (auto / fit-to-page) case. */}
              {format === 'pdf'
                ? t('export.cannotExportSinglePage')
                : t('export.cannotExport', { format: (format ?? 'pdf').toUpperCase() })}
            </span>
          </div>
        )}

        <div className="flex">
          {/* Preview */}
          <div className="flex flex-col items-center justify-center p-4 border-r themed-border min-w-[180px]">
            {previewErrored ? (
              <div className="flex flex-col items-center justify-center gap-2 w-[140px] h-[200px] text-center">
                <AlertTriangle size={18} className="themed-text-muted" />
                <span className="text-[11px] themed-text-muted">{t('export.previewFailed')}</span>
                <button
                  onClick={retry}
                  className="text-xs px-2 py-1 rounded border themed-border themed-text-secondary hover:themed-text transition-colors"
                >
                  {t('export.retry')}
                </button>
              </div>
            ) : exportBlocked ? (
              <div className="flex flex-col items-center justify-center gap-2 w-[140px] h-[200px] text-center">
                <ImageOff size={20} className="text-red-400" />
                <span className="text-[11px] text-red-400">
                  {format === 'pdf'
                    ? t('export.cannotExportSinglePageShort')
                    : t('export.cannotExportShort', { format: (format ?? 'pdf').toUpperCase() })}
                </span>
              </div>
            ) : previewReady ? (
              <>
                <canvas ref={previewRef} className="rounded shadow-lg" />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => setPreviewPage((p) => Math.max(0, p - 1))}
                    disabled={previewPage === 0 || singlePage}
                    className="themed-text-muted hover:themed-text disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-[10px] themed-text-muted tabular-nums">
                    {singlePage ? t('export.pageOf', { page: 1, total: 1 }) : t('export.pageOf', { page: previewPage + 1, total: pageCount })}
                  </span>
                  <button
                    onClick={() => setPreviewPage((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={previewPage >= pageCount - 1 || singlePage}
                    className="themed-text-muted hover:themed-text disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center w-[140px] h-[200px]">
                <Loader2 size={20} className="animate-spin themed-text-muted" />
              </div>
            )}
          </div>

          {/* Options */}
          <div className="flex-1 px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
            {/* Format */}
            <div>
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.format')}</label>
              <div className="flex gap-1.5">
                {(['pdf', 'png', 'jpeg'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => {
                      setFormat(f)
                      if (f !== 'png' && bgColor === 'transparent') setBgColor('auto')
                    }}
                    className={`flex-1 text-xs py-1.5 rounded border transition-colors uppercase ${
                      format === f
                        ? 'bg-brand-600/30 border-brand-500 themed-text'
                        : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* When the export is blocked, options that DON'T affect whether it
                can be produced are disabled (they're moot). Options that DO
                influence feasibility — page size, orientation, margins, fit-to-
                page, capture width — stay enabled so the user can adjust them to
                un-block (e.g. switch a too-tall single-page PDF to multi-page).
                Each moot block is wrapped in its own `disabled` fieldset. */}

            {/* JPEG Quality — does not affect feasibility */}
            {format === 'jpeg' && (
              <fieldset disabled={exportBlocked} className="m-0 p-0 border-0 min-w-0 disabled:opacity-50">
                <label className="text-[10px] uppercase themed-text-muted mb-1 block">
                  {t('export.quality', { pct: Math.round(jpegQuality * 100) })}
                </label>
                <input type="range" min={0.1} max={MAX_JPEG_QUALITY} step={0.05} value={jpegQuality}
                  onChange={(e) => setJpegQuality(Number(e.target.value))} className="w-full" />
              </fieldset>
            )}

            {/* Page Size */}
            {format === 'pdf' && <div>
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.pageSize')}</label>
              <select value={pageSize} onChange={(e) => setPageSize(e.target.value as PdfExportOptions['pageSize'])}
                className="w-full text-sm themed-bg-tertiary themed-text rounded px-2.5 py-1.5 border themed-border outline-none focus:border-brand-500 transition-colors">
                {PAGE_SIZES.map((s) => <option key={s.value} value={s.value}>{t(`export.pageSizes.${s.value}`)}</option>)}
              </select>
            </div>}

            {/* Orientation */}
            {format === 'pdf' && pageSize !== 'auto' && <div>
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.orientation')}</label>
              <div className="flex gap-2">
                {(['portrait', 'landscape'] as const).map((o) => (
                  <button key={o} onClick={() => setOrientation(o)}
                    className={`flex-1 text-sm py-1.5 rounded border transition-colors ${
                      orientation === o ? 'bg-brand-600/30 border-brand-500 themed-text' : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                    }`}>{t(`export.${o}`)}</button>
                ))}
              </div>
            </div>}

            {/* Capture width */}
            <div>
              <label className="text-[10px] uppercase themed-text-muted mb-1 flex items-center justify-between">
                <span>{t('export.captureWidth')}</span>
                <span className="tabular-nums normal-case themed-text-secondary">{captureWidth}px</span>
              </label>
              <input type="range" min={320} max={1920} step={1} value={captureWidth}
                onChange={(e) => setCaptureWidth(Number(e.target.value))} className="w-full cursor-pointer accent-purple-500" />
              <div className="flex justify-between text-[10px] themed-text-muted mt-0.5">
                <button type="button" onClick={() => setCaptureWidth(375)} className="hover:themed-text cursor-pointer">{t('export.mobile')}</button>
                <button type="button" onClick={() => setCaptureWidth(768)} className="hover:themed-text cursor-pointer">{t('export.tablet')}</button>
                <button type="button" onClick={() => setCaptureWidth(1280)} className="hover:themed-text cursor-pointer">{t('export.desktop')}</button>
              </div>
            </div>

            {/* Background — does not affect feasibility */}
            <fieldset disabled={exportBlocked} className="m-0 p-0 border-0 min-w-0 disabled:opacity-50">
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.background')}</label>
              <div className="flex gap-1.5 items-center">
                {format === 'png' && (
                  <button onClick={() => setBgColor('transparent')}
                    className={`flex-1 text-xs py-1.5 rounded border transition-colors ${
                      bgColor === 'transparent' ? 'bg-brand-600/30 border-brand-500 themed-text' : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                    }`}>{t('export.transparent')}</button>
                )}
                <button onClick={() => setBgColor('auto')}
                  className={`flex-1 text-xs py-1.5 rounded border transition-colors ${
                    bgColor === 'auto' ? 'bg-brand-600/30 border-brand-500 themed-text' : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                  }`}>{t('export.auto')}</button>
                <button onClick={() => setBgColor('#ffffff')}
                  className={`flex-1 text-xs py-1.5 rounded border transition-colors ${
                    bgColor !== 'auto' && bgColor !== 'transparent' && bgColor === '#ffffff' ? 'bg-brand-600/30 border-brand-500 themed-text' : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                  }`}>{t('export.white')}</button>
                <div className="relative shrink-0" ref={pickerRef}>
                  <button onClick={() => setShowPicker((p) => !p)}
                    className="w-7 h-7 rounded border themed-border cursor-pointer shrink-0"
                    style={{ background: bgColor === 'auto' ? autoBgColor : bgColor === 'transparent' ? '#000' : bgColor }}
                    title={t('export.customColor')} />
                  {showPicker && (
                    <div className="absolute right-0 top-9 z-50 themed-bg-tertiary rounded-lg shadow-xl border themed-border p-2 flex flex-col gap-2">
                      <HexColorPicker color={bgColor === 'auto' ? autoBgColor : bgColor === 'transparent' ? '#000000' : bgColor} onChange={setBgColor} style={{ width: 160, height: 120 }} />
                      <input type="text" value={bgColor === 'auto' ? autoBgColor : bgColor === 'transparent' ? '#000000' : bgColor}
                        onChange={(e) => { const v = e.target.value; if (/^#[0-9a-fA-F]{6}$/.test(v)) setBgColor(v) }}
                        className="w-full text-[11px] px-1.5 py-1 rounded border themed-border themed-bg-secondary themed-text font-mono text-center" spellCheck={false} />
                    </div>
                  )}
                </div>
              </div>
              {format === 'png' && bgColor !== 'transparent' && (
                <div className="mt-1.5">
                  <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.opacity', { pct: Math.round(bgAlpha * 100) })}</label>
                  <input type="range" min={0} max={1} step={0.05} value={bgAlpha} onChange={(e) => setBgAlpha(Number(e.target.value))} className="w-full" />
                </div>
              )}
            </fieldset>

            {/* Empty area fill — does not affect feasibility */}
            {format === 'pdf' && pageSize !== 'auto' && <fieldset disabled={exportBlocked} className="m-0 p-0 border-0 min-w-0 disabled:opacity-50">
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.emptyAreaFill')}</label>
              <div className="flex gap-1.5">
                {([['extend', t('export.seamless')], ['tile', t('export.layered')], ['solid', t('export.solid')]] as const).map(([value, label]) => (
                  <button key={value} onClick={() => setBgFill(value)}
                    className={`flex-1 text-xs py-1.5 rounded border transition-colors ${
                      bgFill === value ? 'bg-brand-600/30 border-brand-500 themed-text' : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                    }`}>{label}</button>
                ))}
              </div>
            </fieldset>}

            {/* Margins — affects the single-page render box, keep enabled */}
            {format === 'pdf' && pageSize !== 'auto' && <div>
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.margins')}</label>
              <div className="flex gap-1.5">
                {MARGIN_PRESETS.map((m) => (
                  <button key={m.value} onClick={() => setMarginMm(m.value)}
                    className={`flex-1 text-xs py-1.5 rounded border transition-colors ${
                      marginMm === m.value ? 'bg-brand-600/30 border-brand-500 themed-text' : 'themed-bg-tertiary themed-border themed-text-muted hover:themed-text'
                    }`}>{t(m.i18nKey)}</button>
                ))}
              </div>
            </div>}

            {/* Header / footer — needs a margin; does not affect feasibility */}
            {format === 'pdf' && pageSize !== 'auto' && marginMm >= 6 && <fieldset disabled={exportBlocked} className="m-0 p-0 border-0 min-w-0 disabled:opacity-50">
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.headerFooter')}</label>
              <div className="flex flex-col gap-1 text-sm themed-text">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={hfTitle} onChange={(e) => setHfTitle(e.target.checked)} className="rounded" /><span>{t('export.hfTitle')}</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={hfDate} onChange={(e) => setHfDate(e.target.checked)} className="rounded" /><span>{t('export.hfDate')}</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={hfPageNumbers} onChange={(e) => setHfPageNumbers(e.target.checked)} className="rounded" /><span>{t('export.hfPageNumbers')}</span></label>
              </div>
            </fieldset>}

            {/* Page range — multi-page only; does not affect feasibility */}
            {format === 'pdf' && !singlePage && pageCount > 1 && <fieldset disabled={exportBlocked} className="m-0 p-0 border-0 min-w-0 disabled:opacity-50">
              <label className="text-[10px] uppercase themed-text-muted mb-1 block">{t('export.pageRange', { count: pageCount })}</label>
              <input type="text" value={pageRange} onChange={(e) => setPageRange(e.target.value)} placeholder={t('export.pageRangePlaceholder')}
                className={`w-full text-sm themed-bg-tertiary themed-text rounded px-2.5 py-1.5 border outline-none transition-colors ${
                  pageRangeInvalid ? 'border-red-500 focus:border-red-500' : 'themed-border focus:border-brand-500'
                }`} spellCheck={false} />
              {pageRangeInvalid && (
                <p className="text-[11px] text-red-400 mt-1">{t('export.invalidPageRange', { count: pageCount })}</p>
              )}
            </fieldset>}

            {/* Fit to page — affects feasibility (single vs multi page), keep enabled */}
            {format === 'pdf' && pageSize !== 'auto' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={fitToPage} onChange={(e) => setFitToPage(e.target.checked)} className="rounded" />
                <span className="text-sm themed-text">{t('export.fitToPage')}</span>
              </label>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors">{t('export.cancel')}</button>
          <button onClick={handleCopyImage} disabled={generating || !previewReady || copyBlocked}
            title={copyBlocked ? t('export.cannotExport', { format: 'PNG' }) : t('export.copyImage')}
            className="px-2.5 py-1.5 text-sm rounded transition-colors themed-bg-tertiary themed-text-secondary hover:themed-text disabled:opacity-50 flex items-center gap-1.5">
            <Copy size={12} />
          </button>
          {onSaveToDrive && (
            <button onClick={() => runExport(onSaveToDrive)} disabled={generating || !previewReady || exportBlocked || pageRangeInvalid}
              className="px-3 py-1.5 text-sm rounded transition-colors bg-blue-600/80 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1.5">
              {generating && <Loader2 size={12} className="animate-spin" />}
              <Save size={12} />
              {t('export.saveToDrive')}
            </button>
          )}
          <button onClick={() => runExport(onDownload)} disabled={generating || !previewReady || exportBlocked || pageRangeInvalid}
            className="px-3 py-1.5 text-sm rounded transition-colors bg-red-600/80 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5">
            {generating && <Loader2 size={12} className="animate-spin" />}
            <FileDown size={12} />
            {t('export.download')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
